import { useCallback, useEffect, useRef, useState } from 'react';
import { verifyMfaBackupCodeRequest, verifyMfaChallengeRequest } from 'api/requests/auth-mfa-requests';
import IToken from 'types/auth/IToken';
import { MfaFailure, resolveMfaFailure } from '../mfa-helpers';

interface UseMfaChallengeResult {
  submitting: boolean;
  failure: MfaFailure;
  retrySeconds: number;
  verify: (code: string) => Promise<IToken>;
  verifyBackupCode: (code: string) => Promise<IToken>;
  clearFailure: () => void;
}

/**
 * Owns the challenge's two API calls — the authenticator code and the backup code.
 *
 * They are alternatives answering one challenge, not two flows, so they share everything that
 * matters here: the same mfaToken, the same single-success latches, and one backoff counter. The
 * shared counter is not a convenience — the backend raises the same per-enrollment counter for both,
 * so a user who burns three authenticator attempts meets the countdown on their first backup-code
 * submit. Keeping one `retrySeconds` is what makes the UI tell the truth about that.
 *
 * `onFatal` must be referentially stable (wrap it in useCallback) — it is a callback dependency.
 */
export const useMfaChallenge = (mfaToken: string, onFatal: (messageKey: string) => void): UseMfaChallengeResult => {
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<MfaFailure>(null);
  const [retrySeconds, setRetrySeconds] = useState(0);
  const inFlight = useRef(false);
  const succeeded = useRef(false);

  // Backoff countdown, one timeout per tick. Cleared on unmount so a redirect mid-countdown leaves
  // no live timer behind.
  useEffect(() => {
    if (retrySeconds <= 0) {
      return undefined;
    }
    const timer = setTimeout(() => setRetrySeconds(current => Math.max(0, current - 1)), 1000);
    return () => clearTimeout(timer);
  }, [retrySeconds]);

  /**
   * The submit path both codes take. Only the request differs; every latch, failure route and
   * countdown below is shared, which is what keeps the two inputs behaving like one screen.
   */
  const submit = useCallback(
    async (request: () => Promise<IToken>): Promise<IToken> => {
      // Two latches, and the disabled button is not one of them. The server guarantees a double
      // submit cannot create two sessions, but it does that by spending the mfaToken on the first
      // call and answering the second with [MFA] SESSION_EXPIRED — which would send the user back to
      // login reading "start over" immediately after they succeeded. So a second call is never made:
      // `inFlight` blocks one that overlaps, `succeeded` blocks one after a win we already hold.
      // Both are shared across the two endpoints because one challenge row backs them: a backup code
      // submitted while a TOTP request is in flight would race for the same row.
      if (inFlight.current || succeeded.current) {
        return null;
      }
      inFlight.current = true;
      setSubmitting(true);
      setFailure(null);
      try {
        const token = await request();
        succeeded.current = true;
        return token;
      } catch (error) {
        const resolved = resolveMfaFailure(error);
        // SESSION_EXPIRED and BACKUP_CODES_EXHAUSTED get here. A wrong code of either kind —
        // indistinguishable from a replayed one, by design — leaves the challenge open, so the user
        // stays and retries.
        if (resolved.action === 'back-to-login') {
          onFatal(resolved.messageKey);
          return null;
        }
        setFailure(resolved);
        setRetrySeconds(resolved.retryAfterSeconds);
        return null;
      } finally {
        inFlight.current = false;
        // Left disabled on success: the caller is about to store the session and swap the router out
        // from under this screen, and a button that blinks back to life first invites another click.
        if (!succeeded.current) {
          setSubmitting(false);
        }
      }
    },
    [onFatal],
  );

  const verify = useCallback(
    (code: string) => submit(() => verifyMfaChallengeRequest(mfaToken, code)),
    [submit, mfaToken],
  );

  /**
   * Resolves with a token carrying `remainingBackupCodes` — the count left after this code was
   * spent. It is the only place in the app that value is ever populated.
   */
  const verifyBackupCode = useCallback(
    (code: string) => submit(() => verifyMfaBackupCodeRequest(mfaToken, code)),
    [submit, mfaToken],
  );

  const clearFailure = useCallback(() => setFailure(null), []);

  return { submitting, failure, retrySeconds, verify, verifyBackupCode, clearFailure };
};

export default useMfaChallenge;
